/**
 * TextArea component module.
 * @module shared/components/TextArea
 *
 */
import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import PropTypes from 'prop-types';
import styled, {withTheme} from 'styled-components/macro';

import Column from './Column';
import Row from './Row';
import {fill, flexWrap} from './cssUtils';
import TitleLabel from './TitleLabel';
import {getBlackWhiteWithOpacity, getHexWithOpacity} from '../utils/colors';

const _Column = styled(Column)`
  height: ${props => 'calc(' + props.height + '75px)'};
`;

const _ReactQuill = styled(ReactQuill)`
  ${(props) => {
    if (props.readOnly) {
      return (`
        background-color: rgba(0, 0, 0, 0);
        color: ${getBlackWhiteWithOpacity(0.9)};
      `);
    }
    return (`
      background-color: #FFFFFF;
      color: rgba(0, 0, 0, 1);
    `);
  }}
  text-align: left;
  font-family: ${(props) => props.appTheme.bodyText.name};
  font-size: ${(props) => props.appTheme.bodyText.size};
  height: ${props => props.height};
  width: ${props => fill(props.width)};
  opacity: 0.85;

  &.disabled-input-base {
    pointer-events: none;
    opacity: 0.5;
  }
`;

const _TextArea = styled.textarea`
  ${(props) => {
    if (props.readOnly) {
      return (`
      border-width: 3px 3px 3px 0px;
      border: rgba(255, 65, 65, 0);
      padding: 7px 7px 7px 0px;
      color: ${props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
      height: ${props.height || '60px'}; 
      `);
    }
    return (`
      border: 3px solid rgba(255, 65, 65, 0);
      border-radius: 4px;
      padding: 2px;
      color: ${props.theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)'};
      height: ${props.height || '60px'};
    `);
  }}
  background-color: ${props => {
    if (props.theme.neumorphic) {
      return getHexWithOpacity(props.theme.tileBackgroundColor, props.readOnly ? 0 : 1);
    }
    if (props.readOnly) {
      return props.theme.dark ? 'rgba(0, 0, 0, 0)' : '#FFFFFF';
    }
    return props.theme.dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  }};

  ${props => props.theme.neumorphic
    ? `background-image: linear-gradient(to bottom right, rgba(0, 0, 0, ${props.readOnly ? 0 : 0.2}), rgba(0, 0, 0, ${props.readOnly ? 0 : 0.2}))` : ''};
  ${props => props.theme.neumorphic
    ? `box-shadow: -1px -1px 0 0 rgba(0, 0, 0, ${props.readOnly ? 0 : 0.4}),
                   1px 1px 0 0 rgba(255, 255, 255, ${props.readOnly ? 0 : 0.1}); margin: 1px` : ''};
  
  resize: none;
  box-sizing: border-box;
  ${fill()};
  font-size: 12px;
  text-align: left;
  ${flexWrap};
  font-family: ${(props) => props.appTheme.bodyText.name};

  &:focus {
    outline: none;
  }

  &::placeholder {
    font-family: ${props => props.theme.bodyText.name};
    color: ${props => props.theme.dark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
    width: ${props => fill(props.width)};
    font-size: 12px;
    text-align: left;
    ${flexWrap};
    ${fill()};
  }
`;

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    // ['blockquote', 'code-block'],

    [{header: 1}, {header: 2}], // custom button values
    [{list: 'ordered'}, {list: 'bullet'}],
    // [{'script': 'sub'}, {'script': 'super'}],      // superscript/subscript
    [{indent: '-1'}, {indent: '+1'}], // outdent/indent
    // [{'direction': 'rtl'}],                         // text direction

    [{size: ['small', false, 'large', 'huge']}], // custom dropdown
    // [{'header': [1, 2, 3, 4, 5, 6, false]}],
    // [{'font': []}],
    // [{'align': []}],
    ['clean'], // remove formatting button
  ],
  clipboard: {matchVisual: false},
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video',
];

/**
 * Text Area component
 * @name TextArea
 */
const TextArea = (props) => {
  const {
    dataPoint,
    disabled,
    height,
    help,
    isRichText,
    onChange,
    onBlur,
    placeholder,
    readOnly,
    required,
    theme,
    title,
    width,
  } = props;
  const {name, value = ''} = dataPoint;

  const editorType = {
    richText: _ReactQuill,
    text: _TextArea,
  };

  const appTheme = theme;

  let attributes = {
    appTheme,
    disabled,
    height,
    onChange: onChange && (e => onChange(e.target.name, e.target.value)),
    onBlur: onBlur && (e => onBlur(e.target.name, e.target.value)),
    placeholder,
    name,
    readOnly,
    required,
    value,
    width,
  };

  let Editor = editorType.text;

  if (isRichText) {
    Editor = editorType.richText;
    // ReactQuill disables on readOnly and returns value onChange
    attributes = {
      ...attributes,
      theme: readOnly ? 'bubble' : 'snow',
      modules,
      formats,
      readOnly: readOnly || disabled,
      onChange: (val) => onChange && onChange(name, val),
    };
  }

  return <Row margin={'5px'} justifySubRow='left' layout={{grow: 1}} wrap={false}>
    <_Column alignItems={'left'} layout={{grow: 1}} width={width}>
      {title && <TitleLabel title={title} help={help}/>}
      <Editor className={disabled ? 'disabled-input-base' : ''} {...attributes}/>
    </_Column>
  </Row>;
};

export default withTheme(TextArea);

TextArea.propTypes = {
  /**
   * The Text Area data
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * If true, then Text Area is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The height of the Text Area Element
   */
  height: PropTypes.string,
  /**
   * If passed in, then help icon displays per content, title, and type properties
   */
  help: PropTypes.shape({
    /**
     * Help icon description
     */
    description: PropTypes.string,
    /**
     * Help type
     */
    type: PropTypes.oneOf(['action', 'modal', 'tooltip']).isRequired,
    /**
     * If help type is modal, the modal title
     */
    title: PropTypes.string,
    /**
     * If help type is modal, the modal body
     */
    content: PropTypes.node,
    /**
     * If help type is action, the action to perform
     */
    action: PropTypes.func,
    /**
     * If button needs to be customized, then pass the text, action, and data for button
     */
    button: PropTypes.shape({
      text: PropTypes.string,
      action: PropTypes.func,
      data: PropTypes.object,
    }),
  }),
  /**
   * If true, includes rich text editor. Defaults to false.
   */
  isRichText: PropTypes.bool,
  /**
   * The onChange function from Parent for the input field, required if not readOnly
   */
  onChange: PropTypes.func,
  /**
   * The onBlur function from Parent for the input field, required if not readOnly and onChange is not passed
   */
  onBlur: PropTypes.func,
  /**
   * Placeholder
   */
  placeholder: PropTypes.string,
  /**
   * Defaults to false.
   */
  readOnly: PropTypes.bool,
  /**
   * Defaults to false.
   */
  required: PropTypes.bool,
  /**
   * App theme from styled components withTheme
   */
  theme: PropTypes.object,
  /**
   * Text Area Label
   */
  title: PropTypes.string,
  /**
   * The width of the Text Area Element
   */
  width: PropTypes.string,
};
